import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, FileCode2, FileImage, FilePlus2, FileText, Folder, Save, Trash2, Undo2 } from "lucide-react";
import { formatNumber, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Field, InlineAlert, Input, Skeleton, cn, notifySaved, toast, useConfirm } from "@ncp/ui";
import { CodeEditor } from "../../../components/CodeEditor";
import { usePageActions, usePageFile, type PageDetail, type PageFileInfo } from "../api";

export interface CodeFocus { path: string; line?: number | null; n: number }

const languageOf = (path: string): "tsx" | "json" | "text" => (/\.(tsx?|jsx?|mjs|cjs)$/.test(path) ? "tsx" : /\.json$/.test(path) ? "json" : "text");
const iconOf = (f: PageFileInfo) => (f.isBinary ? FileImage : /\.(tsx?|jsx?|mjs|css|html)$/.test(f.path) ? FileCode2 : FileText);
const STARTERS: Record<string, string> = {
  tsx: 'import { connect } from "@ncp/page-sdk";\n\nexport function Section() {\n  return <section className="p-4"></section>;\n}\n',
  md: "",
  json: "{\n}\n",
};

/** A version's files: browse, edit (drafts only) and save; every save is checked at once and the findings show beside the file. */
export function CodeTab({ detail, editable, open, onOpen, focus, onSaved, onDirty }: {
  detail: PageDetail;
  editable: boolean;
  open: string | null;
  onOpen: (path: string) => void;
  focus: CodeFocus | null;
  onSaved: () => void;
  onDirty?: (dirty: boolean) => void;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const confirm = useConfirm();
  const actions = usePageActions();
  const id = detail.page.id;
  const files = detail.files;
  const current = files.find((f) => f.path === open) ?? null;
  const file = usePageFile(id, current?.path ?? null, current?.isBinary ?? false);
  const [buffers, setBuffers] = useState<Record<string, string>>({});
  const base = useRef<Record<string, string>>({});
  const [creating, setCreating] = useState(false);
  const [note, setNote] = useState("");
  const dirty = Object.keys(buffers).length > 0;

  useEffect(() => { onDirty?.(dirty); }, [dirty, onDirty]);
  useEffect(() => { setBuffers({}); base.current = {}; }, [id]);
  useEffect(() => {
    if (!open && files.length) onOpen(files.find((f) => f.path === "index.tsx")?.path ?? files.find((f) => f.path === "page.json")?.path ?? files[0]!.path);
  }, [files, open, onOpen]);

  const groups = useMemo(() => {
    const map = new Map<string, PageFileInfo[]>();
    for (const f of files) {
      const dir = f.path.includes("/") ? f.path.slice(0, f.path.lastIndexOf("/")) : "";
      map.set(dir, [...(map.get(dir) ?? []), f]);
    }
    return [...map.entries()].sort(([a], [b]) => (a === "" ? -1 : b === "" ? 1 : a.localeCompare(b)));
  }, [files]);

  const issues = useMemo(() => (detail.validation?.checks ?? []).flatMap((c) => c.issues.filter((i) => i.path === open).map((i) => ({ ...i, check: c.key }))), [detail.validation, open]);
  const conflict = open && buffers[open] !== undefined && base.current[open] !== undefined && current && base.current[open] !== current.sha256;

  const edit = (value: string) => {
    if (!open || !editable) return;
    if (base.current[open] === undefined && current) base.current[open] = current.sha256;
    setBuffers((b) => (value === file.data?.content ? Object.fromEntries(Object.entries(b).filter(([k]) => k !== open)) : { ...b, [open]: value }));
  };

  const save = useCallback(async () => {
    if (!editable || !dirty) return;
    try {
      const saved = await actions.save.mutateAsync({ id, patch: { upserts: Object.entries(buffers).map(([path, content]) => ({ path, content })), changeNote: note.trim() || null } });
      setBuffers({}); base.current = {}; setNote("");
      const v = saved.validation;
      if (v && v.status === "fail") toast.error(t("admin:pageStudio.code.savedWithErrors", { count: v.errors }));
      else if (v && v.status === "warn") toast.warning(t("admin:pageStudio.code.savedWithWarnings", { count: v.warnings }));
      else notifySaved(saved, t("admin:pageStudio.code.saved"));
      onSaved();
    } catch { /* useSave reported it */ }
  }, [actions.save, buffers, dirty, editable, id, note, onSaved, t]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") { e.preventDefault(); void save(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [save]);

  const remove = async (path: string) => {
    if (!(await confirm({ title: t("admin:pageStudio.code.deleteTitle", { path }), description: t("admin:pageStudio.code.deleteBody"), confirmLabel: t("admin:common.delete"), tone: "destructive", irreversible: false }))) return;
    try {
      await actions.save.mutateAsync({ id, patch: { deletes: [path] } });
      setBuffers((b) => Object.fromEntries(Object.entries(b).filter(([k]) => k !== path)));
      if (open === path) onOpen("page.json");
      onSaved();
    } catch { /* useSave reported it */ }
  };

  const [jump, setJump] = useState<number | null>(null);
  useEffect(() => setJump(null), [open]);
  const value = open ? buffers[open] ?? file.data?.content ?? "" : "";
  const line = jump ?? (focus && focus.path === open ? focus.line ?? null : null);

  return (
    <div className="grid gap-3 lg:grid-cols-[240px_minmax(0,1fr)]">
      <aside className="rounded-xl border border-sand-200 bg-white p-2">
        <div className="mb-1 flex items-center justify-between px-1">
          <span className="text-xs font-semibold text-sand-700">{t("admin:pageStudio.code.files", { count: files.length })}</span>
          {editable ? <Button size="icon-sm" variant="ghost" aria-label={t("admin:pageStudio.code.newFile")} onClick={() => setCreating(true)}><FilePlus2 className="size-4" /></Button> : null}
        </div>
        <nav className="max-h-[70vh] overflow-auto text-sm" dir="ltr">
          {groups.map(([dir, list]) => (
            <div key={dir || "/"} className="mb-1">
              {dir ? <div className="flex items-center gap-1.5 px-1.5 pt-1.5 text-[11px] font-medium text-sand-600"><Folder className="size-3.5" />{dir}/</div> : null}
              {list.map((f) => {
                const Icon = iconOf(f);
                const name = dir ? f.path.slice(dir.length + 1) : f.path;
                const hasIssue = (detail.validation?.checks ?? []).some((c) => c.issues.some((i) => i.path === f.path && i.severity === "error"));
                return (
                  <div key={f.path} className={cn("group flex items-center rounded-md", f.path === open ? "bg-aegold-50 text-aegold-900" : "hover:bg-sand-50")}>
                    <button type="button" onClick={() => onOpen(f.path)} className={cn("flex min-w-0 flex-1 items-center gap-1.5 py-1 text-start", dir ? "ps-5" : "ps-1.5")}>
                      <Icon className="size-3.5 shrink-0 text-sand-500" />
                      <span className="truncate font-mono text-xs">{name}</span>
                      {buffers[f.path] !== undefined ? <span className="size-1.5 shrink-0 rounded-full bg-aegold-500" aria-label={t("admin:common.unsaved")} /> : null}
                      {hasIssue ? <AlertTriangle className="size-3 shrink-0 text-danger-600" /> : null}
                    </button>
                    {editable && f.path !== "page.json" ? (
                      <button type="button" onClick={() => void remove(f.path)} aria-label={t("admin:common.delete")} className="me-1 hidden rounded p-0.5 text-sand-500 hover:text-danger-600 group-hover:block"><Trash2 className="size-3.5" /></button>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ))}
        </nav>
      </aside>

      <section className="min-w-0 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <code className="truncate font-mono text-sm" dir="ltr">{open}</code>
          {current ? <span className="text-xs text-muted-foreground tabular">{formatNumber(current.sizeBytes, locale)} B</span> : null}
          {!editable ? <Badge variant="outline" size="sm">{t("admin:pageStudio.code.readOnly")}</Badge> : null}
          {editable ? (
            <div className="ms-auto flex items-center gap-2">
              {dirty ? <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("admin:pageStudio.code.notePlaceholder")} className="h-8 w-64" /> : null}
              {open && buffers[open] !== undefined ? <Button size="sm" variant="ghost" onClick={() => setBuffers((b) => Object.fromEntries(Object.entries(b).filter(([k]) => k !== open)))}><Undo2 /> {t("admin:pageStudio.code.revert")}</Button> : null}
              <Button size="sm" variant={dirty ? "gold" : "outline"} disabled={!dirty} loading={actions.save.isPending} onClick={() => void save()}>
                <Save /> {Object.keys(buffers).length > 1 ? t("admin:pageStudio.code.saveAll", { count: Object.keys(buffers).length }) : t("admin:common.save")}
              </Button>
            </div>
          ) : null}
        </div>
        {conflict ? (
          <InlineAlert tone="warning" title={t("admin:pageStudio.code.conflictTitle")}>
            {t("admin:pageStudio.code.conflictBody")}{" "}
            <button type="button" className="underline" onClick={() => setBuffers((b) => Object.fromEntries(Object.entries(b).filter(([k]) => k !== open)))}>{t("admin:pageStudio.code.takeTheirs")}</button>
          </InlineAlert>
        ) : null}
        {current?.isBinary ? (
          <div className="rounded-xl border border-sand-200 bg-sand-50 p-6 text-sm text-muted-foreground">{t("admin:pageStudio.code.binary", { type: current.contentType })}</div>
        ) : file.isLoading ? <Skeleton className="h-[60vh]" /> : (
          <CodeEditor key={`${id}:${open}`} value={value} onChange={edit} language={open ? languageOf(open) : "text"} readOnly={!editable} height="62vh" focusLine={line} />
        )}
        {issues.length > 0 ? (
          <ul className="space-y-1 rounded-xl border border-sand-200 bg-white p-2 text-sm">
            {issues.map((i, k) => (
              <li key={k} className="flex items-start gap-2">
                <Badge size="sm" variant={i.severity === "error" ? "danger" : i.severity === "warning" ? "warning" : "outline"}>{t(`admin:pageStudio.severity.${i.severity}`, { defaultValue: i.severity })}</Badge>
                <span className="min-w-0 flex-1">{i.message}</span>
                {i.line ? <button type="button" className="font-mono text-xs text-aegold-700 underline" onClick={() => setJump(i.line ?? null)}>:{i.line}</button> : null}
              </li>
            ))}
          </ul>
        ) : null}
      </section>

      <NewFileDialog open={creating} onOpenChange={setCreating} existing={files.map((f) => f.path)} onCreate={async (path) => {
        const ext = path.split(".").pop() ?? "";
        try {
          await actions.save.mutateAsync({ id, patch: { upserts: [{ path, content: STARTERS[ext === "ts" ? "tsx" : ext] ?? "" }] } });
          setCreating(false);
          onOpen(path);
          onSaved();
        } catch { /* useSave reported it */ }
      }} />
    </div>
  );
}

function NewFileDialog({ open, onOpenChange, existing, onCreate }: { open: boolean; onOpenChange: (o: boolean) => void; existing: string[]; onCreate: (path: string) => Promise<void> }) {
  const { t } = useTranslation();
  const [path, setPath] = useState("");
  const clean = path.trim().replace(/^\/+/, "");
  const invalid = !clean || existing.includes(clean) || !/^[a-zA-Z0-9._\-/]+$/.test(clean) || clean.includes("..");
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setPath(""); }}>
      <DialogContent size="sm">
        <DialogHeader><DialogTitle>{t("admin:pageStudio.code.newFile")}</DialogTitle><DialogDescription>{t("admin:pageStudio.code.newFileHint")}</DialogDescription></DialogHeader>
        <Field label={t("admin:pageStudio.code.path")} error={clean && existing.includes(clean) ? t("admin:pageStudio.code.exists") : undefined}>
          <Input value={path} autoFocus dir="ltr" className="font-mono" placeholder="components/Chart.tsx" onChange={(e) => setPath(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !invalid) void onCreate(clean); }} />
        </Field>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("admin:common.cancel")}</Button>
          <Button variant="gold" disabled={invalid} onClick={() => void onCreate(clean)}><FilePlus2 /> {t("admin:common.create")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
